import { type IconType } from '@/defs/icon'
import type { LabelType, PartialExcept } from '@/defs/misc'

/* Filter Basic: Bracket */
export const FILTER_BRACKETS = ['exhibition', 'core', 'upgraded', 'optimized', 'cedh'] as const
export type FilterBracketType = (typeof FILTER_BRACKETS)[number]
export const FILTER_BRACKET_EMPTY_LABEL = { label: 'Any' } as const
export const FILTER_BRACKET_LABELS: LabelType<FilterBracketType, { labelLong: string }> = {
  cedh: { label: '5', labelLong: 'CEDH' },
  core: { label: '2', labelLong: 'Core' },
  exhibition: { label: '1', labelLong: 'Exhibition' },
  optimized: { label: '4', labelLong: 'Optimized' },
  upgraded: { label: '3', labelLong: 'Upgraded' },
}
export const isFilterBracketType = (slug?: string): slug is FilterBracketType =>
  FILTER_BRACKETS.includes(slug as FilterBracketType)
export const FILTER_BRACKET_LABEL_SHORT = ['1', '2', '3', '4', '5'] as const
export const FILTER_BRACKET_LOOKUP: Record<(typeof FILTER_BRACKET_LABEL_SHORT)[number], FilterBracketType> = {
  '1': 'exhibition',
  '2': 'core',
  '3': 'upgraded',
  '4': 'optimized',
  '5': 'cedh',
} as const

/* Filter Basic: Budget */
export const FILTER_BUDGETS = ['budget', 'expensive'] as const
export type FilterBudgetType = (typeof FILTER_BUDGETS)[number]
export const FILTER_BUDGET_EMPTY_LABEL = { label: 'Any' } as const
export const FILTER_BUDGET_LABELS: LabelType<FilterBudgetType, { labelLong: string }> = {
  budget: { label: '$', labelLong: 'Inexpensive' },
  expensive: { label: '$$$', labelLong: 'Expensive' },
}
export const isFilterBudgetType = (slug?: string): slug is FilterBudgetType =>
  FILTER_BUDGETS.includes(slug as FilterBudgetType)

/* Filter Basic: Timeframe */
export const FILTER_TIMEFRAMES = ['month', 'week'] as const
export type FilterTimeframeType = (typeof FILTER_TIMEFRAMES)[number]
export const FILTER_TIMEFRAME_EMPTY_LABEL = { label: '2 Years', labelLong: 'Past Two Years' } as const
export const FILTER_TIMEFRAME_LABELS: LabelType<FilterTimeframeType, { labelLong: string }> = {
  month: { label: 'Month', labelLong: 'Past Month' },
  week: { label: 'Week', labelLong: 'Past Week' },
} as const
export const isFilterTimeframeType = (slug?: string): slug is FilterTimeframeType =>
  FILTER_TIMEFRAMES.includes(slug as FilterTimeframeType)

/**               **/
/* Filter Advanced */
/**               **/

/* Filter Advanced: Card */
export const FILTER_ADVANCED_CARD_SLUGS = ['withCard', 'withoutCard'] as const
export type FilterAdvancedCardSlugType = (typeof FILTER_ADVANCED_CARD_SLUGS)[number]
export const isFilterAdvancedCardSlugType = (slug?: string): slug is FilterAdvancedCardSlugType =>
  FILTER_ADVANCED_CARD_SLUGS.includes(slug as FilterAdvancedCardSlugType)

export type FilterAdvancedCardType = {
  card: string
  slug: FilterAdvancedCardSlugType
}
export type FilterAdvancedCardPartialType = PartialExcept<FilterAdvancedCardType, 'slug'>
export const isFilterAdvancedCardPartialType = (
  filter?: FilterAdvancedPartialType,
): filter is FilterAdvancedCardPartialType => isFilterAdvancedCardSlugType(filter?.slug)
export const isFilterAdvancedCardType = (filter?: FilterAdvancedPartialType): filter is FilterAdvancedCardType =>
  isFilterAdvancedCardPartialType(filter) && !!filter.card

/* Filter Advanced: Count */
export const FILTER_ADVANCED_COUNT_SLUGS = ['c', 'i', 's', 'a', 'e', 'b', 'p', 'l', 'n', 'g'] as const
export type FilterAdvancedCountSlugType = (typeof FILTER_ADVANCED_COUNT_SLUGS)[number]
export const isFilterAdvancedCountSlugType = (slug?: string): slug is FilterAdvancedCountSlugType =>
  FILTER_ADVANCED_COUNT_SLUGS.includes(slug as FilterAdvancedCountSlugType)

export const FILTER_ADVANCED_COUNT_VALUE_OPERATORS = ['gt', 'lt', 'eq'] as const
export type FilterAdvancedCountValueOperatorsType = (typeof FILTER_ADVANCED_COUNT_VALUE_OPERATORS)[number]
export const FILTER_ADVANCED_COUNT_VALUE_OPERATOR_LABELS: LabelType<
  FilterAdvancedCountValueOperatorsType,
  { labelLong: string }
> = {
  eq: { label: '=', labelLong: 'Exactly' },
  gt: { label: '≥', labelLong: 'At least' },
  lt: { label: '≤', labelLong: 'At most' },
}
export const isFilterAdvancedCountValueOperatorsType = (
  operator?: string,
): operator is FilterAdvancedCountValueOperatorsType =>
  FILTER_ADVANCED_COUNT_VALUE_OPERATORS.includes(operator as FilterAdvancedCountValueOperatorsType)
export type FilterAdvancedCountType = {
  number: number
  operator: FilterAdvancedCountValueOperatorsType
  slug: FilterAdvancedCountSlugType
}
export type FilterAdvancedCountPartialType = PartialExcept<FilterAdvancedCountType, 'slug'>
export const isFilterAdvancedCountPartialType = (
  filter?: FilterAdvancedPartialType,
): filter is FilterAdvancedCountPartialType => isFilterAdvancedCountSlugType(filter?.slug)
export const FILTER_ADVANCED_COUNT_NUMBER_MIN = 0
export const FILTER_ADVANCED_COUNT_NUMBER_MAX = 99
export const isFilterAdvancedCountNumberType = (number?: number): number is number =>
  number !== undefined &&
  !isNaN(number) &&
  number >= FILTER_ADVANCED_COUNT_NUMBER_MIN &&
  number <= FILTER_ADVANCED_COUNT_NUMBER_MAX
export const isFilterAdvancedCountType = (filter?: FilterAdvancedPartialType): filter is FilterAdvancedCountType =>
  isFilterAdvancedCountPartialType(filter) &&
  isFilterAdvancedCountNumberType(filter.number) &&
  isFilterAdvancedCountValueOperatorsType(filter.operator)

/* Filter Advanced: Price */
export const FILTER_ADVANCED_PRICE_SLUGS = ['d'] as const
export type FilterAdvancedPriceSlugType = (typeof FILTER_ADVANCED_PRICE_SLUGS)[number]
export const isFilterAdvancedPriceSlugType = (slug?: string): slug is FilterAdvancedPriceSlugType =>
  FILTER_ADVANCED_PRICE_SLUGS.includes(slug as FilterAdvancedPriceSlugType)

export const FILTER_ADVANCED_PRICE_VALUE_OPERATORS = ['gt', 'lt'] as const
export type FilterAdvancedPriceValueOperatorsType = (typeof FILTER_ADVANCED_PRICE_VALUE_OPERATORS)[number]
export const FILTER_ADVANCED_PRICE_VALUE_OPERATOR_LABELS: LabelType<
  FilterAdvancedPriceValueOperatorsType,
  { labelLong: string }
> = {
  gt: { label: '≥', labelLong: 'At least' },
  lt: { label: '≤', labelLong: 'At most' },
}
export const isFilterAdvancedPriceValueOperatorsType = (
  operator?: string,
): operator is FilterAdvancedPriceValueOperatorsType =>
  FILTER_ADVANCED_PRICE_VALUE_OPERATORS.includes(operator as FilterAdvancedPriceValueOperatorsType)
export type FilterAdvancedPriceType = {
  number: number
  operator: FilterAdvancedPriceValueOperatorsType
  slug: FilterAdvancedPriceSlugType
}
export type FilterAdvancedPricePartialType = PartialExcept<FilterAdvancedPriceType, 'slug'>
export const isFilterAdvancedPricePartialType = (
  filter?: FilterAdvancedPartialType,
): filter is FilterAdvancedPricePartialType => isFilterAdvancedPriceSlugType(filter?.slug)
export const FILTER_ADVANCED_PRICE_NUMBER_MIN = 0
export const isFilterAdvancedPriceNumberType = (number?: number): number is number =>
  number !== undefined && !isNaN(number) && number >= FILTER_ADVANCED_PRICE_NUMBER_MIN
export const isFilterAdvancedPriceType = (filter?: FilterAdvancedPartialType): filter is FilterAdvancedPriceType =>
  isFilterAdvancedPricePartialType(filter) &&
  isFilterAdvancedPriceNumberType(filter.number) &&
  isFilterAdvancedPriceValueOperatorsType(filter.operator)

/* Filter Advanced */
export const FILTER_ADVANCED_SLUGS = [
  ...FILTER_ADVANCED_CARD_SLUGS,
  ...FILTER_ADVANCED_COUNT_SLUGS,
  ...FILTER_ADVANCED_PRICE_SLUGS,
] as const
export type FilterAdvancedSlugType = (typeof FILTER_ADVANCED_SLUGS)[number]
export const isFilterAdvancedSlugType = (slug?: string): slug is FilterAdvancedSlugType =>
  FILTER_ADVANCED_SLUGS.includes(slug as FilterAdvancedSlugType)

export type FilterAdvancedType = FilterAdvancedCardType | FilterAdvancedCountType | FilterAdvancedPriceType
export type FilterAdvancedPartialType =
  Partial<FilterAdvancedCardType> | Partial<FilterAdvancedCountType> | Partial<FilterAdvancedPriceType>
export const isFilterAdvancedType = (filter?: FilterAdvancedPartialType): filter is FilterAdvancedType =>
  isFilterAdvancedCardType(filter) || isFilterAdvancedCountType(filter) || isFilterAdvancedPriceType(filter)

export const withFilterAdvancedDefaults = (
  slug: FilterAdvancedSlugType,
  filter?: FilterAdvancedPartialType,
): FilterAdvancedPartialType => {
  if (isFilterAdvancedCountSlugType(slug)) {
    return { operator: 'gt', ...(isFilterAdvancedCountPartialType(filter) ? filter : undefined), slug }
  }
  if (isFilterAdvancedPriceSlugType(slug)) {
    return { operator: 'gt', ...(isFilterAdvancedPricePartialType(filter) ? filter : undefined), slug }
  }
  return { ...(isFilterAdvancedCardPartialType(filter) ? filter : undefined), slug }
}

export type FilterAdvancedSlugLabelType = LabelType<FilterAdvancedSlugType, { icon: IconType; labelLong?: string }>
export const FILTER_ADVANCED_SLUG_LABELS: FilterAdvancedSlugLabelType = {
  a: { icon: 'type-artifact', label: 'Artifacts in Deck' },
  b: { icon: 'type-battle', label: 'Battles in Deck' },
  c: { icon: 'type-creature', label: 'Creatures in Deck' },
  d: { icon: 'dollarSign', label: 'Deck Price' },
  e: { icon: 'type-enchantment', label: 'Enchantments in Deck' },
  g: { icon: 'gem', label: 'Game Changers in Deck' },
  i: { icon: 'type-instant', label: 'Instants in Deck' },
  l: { icon: 'type-land', label: 'Lands in Deck' },
  n: { icon: 'salt', label: 'Salt Score of Deck' },
  p: { icon: 'type-planeswalker', label: 'Planeswalkers in Deck' },
  s: { icon: 'type-sorcery', label: 'Sorceries in Deck' },
  withCard: { icon: 'check', label: 'With Card' },
  withoutCard: { icon: 'ban', label: 'Without Card' },
}

export const FILTER_ADVANCED_MAX = 8
